import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet'
import { api } from '@pilot-hub/api-client'
import { ConfirmDialog } from '@pilot-hub/ui'
import L from 'leaflet'
import { Download, Pause, Play, Scissors, Trash2, Video } from 'lucide-react'
import 'leaflet/dist/leaflet.css'

// Videoaufnahme eines Fluges (Livebild, von MediaMTX aufgezeichnet, Medien-Worker) mit synchroner Karte und
// gesicherten Ausschnitten (30 s bis 2 Min., eigene MP4-Datei, Download, Löschen).
// Diese Datei ist in apps/admin, apps/fuehrung und apps/pilot identisch.
// Zeiten sind Ortszeit ohne Zeitzone (wie fluege.startzeit und flug_telemetrie.zeit) und werden einheitlich als lokale Zeit gelesen.

interface Segment { id: number; nr: number; start: string; dauer_s: number; groesse: number; url: string }
interface Video { id: number; start: string; ende: string; groesse?: number; geloescht_am: string | null; segmente: Segment[] }
interface AusschnittDaten {
  id: number; titel: string; bemerkung: string | null; start: string; dauer_s: number; groesse: number
  erstellt_von: string | null; erstellt_am: string | null; url: string; dateiname: string
}
interface Antwort { flug: { id: number; flug_nr: string | null; start: string | null; ende: string | null }; videos: Video[]; ausschnitte: AusschnittDaten[] }
interface Abschnitt { id: number; t0: number; dauer: number; url: string }
type Punkt = [number, number, number | null, string, number | null]

const MIN_S = 30, MAX_S = 120
const ms = (s: string) => new Date(s).getTime()
const zwei = (n: number) => String(n).padStart(2, '0')
const lokalIso = (t: number) => {
  const d = new Date(t)
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}T${zwei(d.getHours())}:${zwei(d.getMinutes())}:${zwei(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`
}
const uhr = (t: number) => new Date(t).toLocaleTimeString('de-DE')
const dauerText = (s: number) => `${Math.floor(s / 60)}:${zwei(Math.round(s % 60))}`
const mb = (b: number) => (b / 1048576).toLocaleString('de-DE', { maximumFractionDigits: b > 1e8 ? 0 : 1 }) + ' MB'

const drohne = L.divIcon({
  className: '', iconSize: [22, 22], iconAnchor: [11, 11],
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#f59e0b;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>',
})

function Ausrichten({ punkte }: { punkte: [number, number][] }) {
  const map = useMap()
  useEffect(() => {
    const f = () => { map.invalidateSize(); if (punkte.length > 1) map.fitBounds(L.latLngBounds(punkte), { padding: [24, 24], maxZoom: 18 }) }
    f(); const t = setTimeout(f, 250); return () => clearTimeout(t)
  }, [map, punkte])
  return null
}
function Folgen({ pos }: { pos: [number, number] | null }) {
  const map = useMap()
  useEffect(() => { if (pos && !map.getBounds().pad(-0.15).contains(pos)) map.panTo(pos, { animate: true }) }, [map, pos])
  return null
}

export function FlugAufnahme({ flug, darfLoeschen = false }: { flug: { id: number }; darfLoeschen?: boolean }) {
  const [daten, setDaten] = useState<Antwort | null>(null)
  const [weg, setWeg] = useState<Punkt[]>([])
  const [quelle, setQuelle] = useState<number | null>(null)       // null = Gesamtaufnahme, sonst Ausschnitt-ID
  const [t, setT] = useState(0)
  const [spielt, setSpielt] = useState(false)
  const [tempo, setTempo] = useState<1 | 2>(1)               // Wiedergabegeschwindigkeit, gilt für alle Segmente des Flugs
  const [auswahl, setAuswahl] = useState<{ von: number | null; bis: number | null }>({ von: null, bis: null })
  const [titel, setTitel] = useState('')
  const [bemerkung, setBemerkung] = useState('')
  const [beschaeftigt, setBeschaeftigt] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [loeschen, setLoeschen] = useState<AusschnittDaten | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const aktuell = useRef<Abschnitt | null>(null)
  const ziel = useRef<{ s: number; spielen: boolean } | null>(null)
  const tempoRef = useRef<1 | 2>(1)
  const tempoSetzen = (n: 1 | 2) => {
    tempoRef.current = n; setTempo(n)
    const v = videoRef.current
    if (v) { v.defaultPlaybackRate = n; v.playbackRate = n }
  }
  // Der Browser setzt playbackRate bei Quellenwechsel zurück: nach dem Laden wieder anwenden
  const tempoAnwenden = () => { const v = videoRef.current; if (v) { v.defaultPlaybackRate = tempoRef.current; v.playbackRate = tempoRef.current } }

  const laden = useCallback(async () => {
    try { setDaten((await api.get(`/dji/aufnahmen/flug/${flug.id}`)).data as Antwort) } catch { setDaten(null) }
  }, [flug.id])
  useEffect(() => { setQuelle(null); setDaten(null); laden() }, [laden])
  useEffect(() => {
    api.get(`/fluege/${flug.id}/flugweg`).then(r => setWeg(((r.data as { punkte: Punkt[] }).punkte) ?? [])).catch(() => setWeg([]))
  }, [flug.id])

  const aufnahme: Abschnitt[] = useMemo(() => (daten?.videos ?? []).flatMap(v => v.segmente)
    .map(s => ({ id: s.id, t0: ms(s.start), dauer: s.dauer_s * 1000, url: s.url })).sort((a, b) => a.t0 - b.t0), [daten])
  const clip = quelle !== null ? daten?.ausschnitte.find(a => a.id === quelle) ?? null : null
  const abschnitte: Abschnitt[] = useMemo(() => clip ? [{ id: -clip.id, t0: ms(clip.start), dauer: clip.dauer_s * 1000, url: clip.url }] : aufnahme, [clip, aufnahme])
  const von = abschnitte[0]?.t0 ?? 0
  const bis = abschnitte.length ? Math.max(...abschnitte.map(a => a.t0 + a.dauer)) : 0
  const spanne = Math.max(1, bis - von)

  const springe = useCallback((zeit: number, spielen: boolean) => {
    const v = videoRef.current
    if (!v || !abschnitte.length) return
    const treffer = [...abschnitte].reverse().find(a => a.t0 <= zeit && zeit <= a.t0 + a.dauer)
      ?? abschnitte.find(a => a.t0 > zeit) ?? abschnitte[abschnitte.length - 1]
    const s = Math.min(Math.max(0, (zeit - treffer.t0) / 1000), Math.max(0, treffer.dauer / 1000 - 0.05))
    setT(treffer.t0 + s * 1000)
    if (aktuell.current?.id !== treffer.id) {
      aktuell.current = treffer
      ziel.current = { s, spielen }
      v.src = treffer.url
      v.load()
    } else {
      v.currentTime = s
      if (spielen) v.play().catch(() => setSpielt(false))
    }
  }, [abschnitte])

  // Quelle (Gesamtaufnahme/Ausschnitt) gewechselt oder neu geladen: Anfang anzeigen
  useEffect(() => {
    aktuell.current = null; setSpielt(false)
    if (abschnitte.length) springe(abschnitte[0].t0, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quelle, aufnahme.length])

  const beiMetadaten = () => {
    const v = videoRef.current, z = ziel.current
    tempoAnwenden()
    if (!v || !z) return
    ziel.current = null
    v.currentTime = z.s
    if (z.spielen) v.play().catch(() => setSpielt(false))
  }
  const beiZeit = () => {
    const v = videoRef.current, a = aktuell.current
    if (v && a && !ziel.current) setT(a.t0 + v.currentTime * 1000)
  }
  const beiEnde = () => {
    const a = aktuell.current
    const naechster = a && abschnitte.find(x => x.t0 >= a.t0 + a.dauer - 500 && x.id !== a.id)
    if (naechster) springe(naechster.t0, true); else setSpielt(false)
  }
  const umschalten = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) { v.play().then(() => setSpielt(true)).catch(() => setSpielt(false)) } else { v.pause(); setSpielt(false) }
  }

  // Drohnenposition zur Abspielzeit (linear zwischen den Flugweg-Punkten)
  const zeiten = useMemo(() => weg.map(p => ms(p[3])), [weg])
  const pos: [number, number] | null = useMemo(() => {
    if (!weg.length || !t) return null
    let lo = 0, hi = zeiten.length - 1
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (zeiten[m] <= t) lo = m; else hi = m - 1 }
    const a = weg[lo], b = weg[Math.min(lo + 1, weg.length - 1)]
    const za = zeiten[lo], zb = zeiten[Math.min(lo + 1, weg.length - 1)]
    const f = zb > za && t > za ? Math.min(1, (t - za) / (zb - za)) : 0
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]
  }, [weg, zeiten, t])
  const linie = useMemo(() => weg.map(p => [p[0], p[1]] as [number, number]), [weg])
  const gefluegen = useMemo(() => {
    let n = 0; while (n < zeiten.length && zeiten[n] <= t) n++
    return linie.slice(0, n)
  }, [linie, zeiten, t])

  const sichern = async () => {
    if (auswahl.von === null || auswahl.bis === null) return
    setBeschaeftigt(true); setMeldung(null)
    try {
      await api.post('/dji/aufnahmen/ausschnitt', { flug_id: flug.id, start: lokalIso(auswahl.von), dauer_s: (auswahl.bis - auswahl.von) / 1000, titel: titel.trim(), bemerkung: bemerkung.trim() || null })
      setAuswahl({ von: null, bis: null }); setTitel(''); setBemerkung('')
      setMeldung({ art: 'ok', text: 'Ausschnitt gesichert' })
      const keep = quelle; await laden(); setQuelle(keep)
    } catch (e: any) { setMeldung({ art: 'fehler', text: e?.response?.data?.detail || 'Ausschnitt konnte nicht gesichert werden' }) }
    finally { setBeschaeftigt(false) }
  }
  const herunterladen = async (pfad: string) => {
    try { window.location.href = ((await api.get(pfad)).data as { url: string }).url }
    catch (e: any) { setMeldung({ art: 'fehler', text: e?.response?.data?.detail || 'Download nicht möglich' }) }
  }
  const loeschenBestaetigt = async () => {
    if (!loeschen) return
    try { await api.delete(`/dji/aufnahmen/ausschnitt/${loeschen.id}`); if (quelle === loeschen.id) setQuelle(null); setLoeschen(null); await laden() }
    catch (e: any) { setLoeschen(null); setMeldung({ art: 'fehler', text: e?.response?.data?.detail || 'Löschen fehlgeschlagen' }) }
  }

  const hatAufnahme = aufnahme.length > 0
  const geloescht = (daten?.videos ?? []).some(v => v.geloescht_am)
  if (!daten || (!hatAufnahme && !daten.ausschnitte.length && !geloescht)) return null

  const dauerAuswahl = auswahl.von !== null && auswahl.bis !== null ? (auswahl.bis - auswahl.von) / 1000 : null
  const gueltig = dauerAuswahl !== null && dauerAuswahl >= MIN_S - 0.5 && dauerAuswahl <= MAX_S + 0.5 && titel.trim().length > 0
  const proz = (x: number) => `${Math.max(0, Math.min(100, ((x - von) / spanne) * 100))}%`
  const luecken = abschnitte.slice(1).map((a, i) => ({ von: abschnitte[i].t0 + abschnitte[i].dauer, bis: a.t0 })).filter(l => l.bis - l.von > 1500)
  const knopf = 'min-h-[40px] rounded-lg border border-dark-border px-3 text-sm text-dark-text hover:bg-dark-bg-tertiary disabled:opacity-40'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-dark-text"><Video className="h-4 w-4" />Videoaufnahme</div>
          <div className="text-xs text-dark-text-muted">
            {hatAufnahme ? `Livebild ${uhr(aufnahme[0].t0)} – ${uhr(Math.max(...aufnahme.map(a => a.t0 + a.dauer)))} Uhr` : 'Die Aufnahme wurde nach Ablauf der Aufbewahrungsfrist gelöscht; gesicherte Ausschnitte bleiben erhalten.'}
          </div>
        </div>
        {clip && hatAufnahme && <button type="button" className={knopf} onClick={() => setQuelle(null)}>Zurück zur Gesamtaufnahme</button>}
        {!clip && hatAufnahme && (daten.videos.find(v => !v.geloescht_am)) && (
          <button type="button" className={knopf} onClick={() => herunterladen(`/dji/aufnahmen/video/${daten.videos.find(v => !v.geloescht_am)!.id}/link`)}>
            <Download className="mr-1 inline h-4 w-4" />Gesamte Aufnahme herunterladen
          </button>)}
      </div>

      {abschnitte.length > 0 && (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            {clip && <div className="rounded-lg bg-primary-600/20 px-3 py-1.5 text-xs text-dark-text">Ausschnitt: {clip.titel}</div>}
            <video ref={videoRef} playsInline preload="metadata" className="aspect-video w-full rounded-lg bg-black"
              onLoadedMetadata={beiMetadaten} onLoadStart={tempoAnwenden} onTimeUpdate={beiZeit} onEnded={beiEnde} onPlay={() => { tempoAnwenden(); setSpielt(true) }} onPause={() => setSpielt(false)}
              onError={() => { if (aktuell.current) laden() }} />
            <div className="flex items-center gap-3">
              <button type="button" onClick={umschalten} aria-label={spielt ? 'Pause' : 'Abspielen'}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
                {spielt ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
              <div role="group" aria-label="Wiedergabegeschwindigkeit" className="flex shrink-0 overflow-hidden rounded-lg border border-dark-border">
                {([1, 2] as const).map(n => (
                  <button key={n} type="button" aria-pressed={tempo === n} onClick={() => tempoSetzen(n)}
                    className={`min-h-[44px] min-w-[44px] px-3 text-sm font-semibold tabular-nums ${tempo === n ? 'bg-primary-600 text-white' : 'text-dark-text hover:bg-dark-bg-tertiary'}`}>{n}x</button>))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="relative h-8">
                  <div className="absolute inset-x-0 top-3 h-2 overflow-hidden rounded-full bg-dark-border">
                    {luecken.map((l, i) => <div key={i} className="absolute top-0 h-full bg-black/60" style={{ left: proz(l.von), width: `calc(${proz(l.bis)} - ${proz(l.von)})` }} />)}
                    {!clip && (daten.ausschnitte).map(a => {
                      const a0 = ms(a.start)
                      return <div key={a.id} className="absolute top-0 h-full bg-green-500/80" style={{ left: proz(a0), width: `calc(${proz(a0 + a.dauer_s * 1000)} - ${proz(a0)})` }} title={a.titel} />
                    })}
                    {auswahl.von !== null && <div className="absolute top-0 h-full bg-amber-400/90" style={{ left: proz(auswahl.von), width: `calc(${proz(auswahl.bis ?? auswahl.von + 1000)} - ${proz(auswahl.von)})`, minWidth: 3 }} />}
                  </div>
                  <input type="range" min={von} max={bis} step={500} value={Math.min(Math.max(t, von), bis)} aria-label="Zeitachse"
                    onChange={e => springe(Number(e.target.value), spielt)} className="absolute inset-0 h-8 w-full cursor-pointer opacity-70" />
                </div>
                <div className="flex justify-between text-[11px] tabular-nums text-dark-text-muted">
                  <span>{uhr(Math.min(Math.max(t, von), bis))} Uhr</span>
                  <span>{dauerText(Math.max(0, t - von) / 1000)} / {dauerText(spanne / 1000)}</span>
                </div>
              </div>
            </div>
            {!clip && (
              <div className="rounded-lg border border-dark-border p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-dark-text"><Scissors className="h-4 w-4" />Ausschnitt sichern ({MIN_S} Sekunden bis {MAX_S / 60} Minuten)</div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" className={knopf} onClick={() => setAuswahl({ von: t, bis: auswahl.bis !== null && auswahl.bis > t ? auswahl.bis : null })}>Start hier</button>
                  <button type="button" className={knopf} disabled={auswahl.von === null || t <= auswahl.von} onClick={() => setAuswahl(a => ({ ...a, bis: t }))}>Ende hier</button>
                  {[30, 60, 120].map(s => (
                    <button key={s} type="button" className={knopf} disabled={auswahl.von === null} onClick={() => setAuswahl(a => ({ ...a, bis: Math.min(bis, (a.von as number) + s * 1000) }))}>
                      {s === 30 ? '30 s' : s === 60 ? '1 Min.' : '2 Min.'}
                    </button>))}
                </div>
                <div className="text-xs text-dark-text-muted">
                  {auswahl.von === null ? 'Zum Markieren die Wiedergabe an die gewünschte Stelle bringen und „Start hier“ wählen.'
                    : `${uhr(auswahl.von)}${auswahl.bis !== null ? ` – ${uhr(auswahl.bis)} Uhr (${dauerText(dauerAuswahl!)} Min.)` : ' – Ende noch offen'}`}
                  {dauerAuswahl !== null && (dauerAuswahl < MIN_S - 0.5 || dauerAuswahl > MAX_S + 0.5) && <span className="ml-1 text-amber-400">Erlaubt sind {MIN_S} Sekunden bis {MAX_S / 60} Minuten.</span>}
                </div>
                <input className="input w-full" placeholder="Titel (Pflicht)" maxLength={150} value={titel} onChange={e => setTitel(e.target.value)} />
                <textarea className="input min-h-[60px] w-full resize-y" placeholder="Bemerkung (optional)" value={bemerkung} onChange={e => setBemerkung(e.target.value)} />
                <button type="button" disabled={!gueltig || beschaeftigt} onClick={sichern}
                  className="min-h-[40px] rounded-lg bg-primary-600 px-4 text-sm font-medium text-white disabled:opacity-40">{beschaeftigt ? 'Wird gesichert…' : 'Ausschnitt sichern'}</button>
              </div>)}
          </div>

          <div className="relative z-0 h-[300px] overflow-hidden rounded-lg border border-dark-border lg:h-auto lg:min-h-[340px]">
            {linie.length > 0 ? (
              <MapContainer center={linie[0]} zoom={16} className="h-full w-full" scrollWheelZoom>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" maxNativeZoom={19} maxZoom={20} />
                <Polyline positions={linie} pathOptions={{ color: '#ffffff', weight: 5, opacity: 0.9 }} />
                <Polyline positions={linie} pathOptions={{ color: '#94a3b8', weight: 3 }} />
                {gefluegen.length > 1 && <Polyline positions={gefluegen} pathOptions={{ color: '#d9282f', weight: 4 }} />}
                {pos && <Marker position={pos} icon={drohne} />}
                <Ausrichten punkte={linie} />
                <Folgen pos={pos} />
              </MapContainer>
            ) : <div className="flex h-full items-center justify-center p-4 text-center text-sm text-dark-text-muted">Kein Flugweg aufgezeichnet – die Karte steht nur bei Flügen über DJI Pilot 2 zur Verfügung.</div>}
          </div>
        </div>
      )}

      {meldung && <div className={`rounded-lg border px-3 py-2 text-sm ${meldung.art === 'ok' ? 'border-green-500/30 bg-green-500/10 text-green-200' : 'border-red-500/30 bg-red-500/10 text-red-200'}`}>{meldung.text}</div>}

      {daten.ausschnitte.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-dark-text">Gesicherte Ausschnitte ({daten.ausschnitte.length})</div>
          {daten.ausschnitte.map(a => (
            <div key={a.id} className={`rounded-lg border px-3 py-2 ${quelle === a.id ? 'border-primary-600' : 'border-dark-border'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-dark-text">{a.titel}</div>
                  <div className="text-xs text-dark-text-muted">{new Date(a.start).toLocaleString('de-DE')} Uhr · {dauerText(a.dauer_s)} Min. · {mb(a.groesse)}{a.erstellt_von ? ` · ${a.erstellt_von}` : ''}</div>
                  {a.bemerkung && <div className="mt-1 whitespace-pre-wrap text-sm text-dark-text-muted">{a.bemerkung}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={knopf} onClick={() => { setQuelle(a.id) }}><Play className="mr-1 inline h-4 w-4" />Ansehen</button>
                  <button type="button" className={knopf} onClick={() => herunterladen(`/dji/aufnahmen/ausschnitt/${a.id}/link`)}><Download className="mr-1 inline h-4 w-4" />Herunterladen</button>
                  {darfLoeschen && <button type="button" className={`${knopf} text-red-300`} onClick={() => setLoeschen(a)}><Trash2 className="mr-1 inline h-4 w-4" />Löschen</button>}
                </div>
              </div>
            </div>))}
        </div>
      )}

      <ConfirmDialog isOpen={!!loeschen} onClose={() => setLoeschen(null)} onConfirm={loeschenBestaetigt}
        title="Ausschnitt löschen?" message={`Der gesicherte Ausschnitt „${loeschen?.titel ?? ''}“ wird endgültig gelöscht und kann nicht wiederhergestellt werden.`} confirmText="Löschen" />
    </div>
  )
}
